import axios from "axios";

const baseUrl = "https://tl-api.titancapital.au";

function authHeaders(token) {
    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
}

// --- Global audit feed -------------------------------------------------

async function getAllAudit(token) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/audit`,
        authHeaders(token),
    );
    return res?.data;
}

// --- Funders ---------------------------------------------------------------

async function getFunders(token, { showDeleted = false } = {}) {
    const res = await axios.get(`${baseUrl}/funder-allocation/funders`, {
        ...authHeaders(token),
        params: { show_deleted: showDeleted },
    });
    return res?.data;
}

async function getFunder(token, funderId) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/funders/${funderId}`,
        authHeaders(token),
    );
    return res?.data;
}

async function createFunder(token, { name, ref }) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/funders`,
        { name, ref },
        authHeaders(token),
    );
    return res?.data;
}

async function updateFunder(token, funderId, { name, ref }) {
    const res = await axios.put(
        `${baseUrl}/funder-allocation/funders/${funderId}`,
        { name, ref },
        authHeaders(token),
    );
    return res?.data;
}

async function deleteFunder(token, funderId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/funders/${funderId}`,
        authHeaders(token),
    );
    return res?.data;
}

async function restoreFunder(token, funderId) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/funders/${funderId}/restore`,
        null,
        authHeaders(token),
    );
    return res?.data;
}

async function getFunderAllocations(
    token,
    funderId,
    { loanStatus, showRetired = false, showDeleted = false } = {},
) {
    const params = { show_retired: showRetired, show_deleted: showDeleted };
    if (loanStatus) params.loan_status = loanStatus;
    const res = await axios.get(
        `${baseUrl}/funder-allocation/funders/${funderId}/allocations`,
        // Axios's default serializer renders array params as `key[]=...`,
        // but the backend's FastAPI Query(list[str]) only binds repeated
        // `key=...&key=...` - `indexes: null` renders it that way instead.
        { ...authHeaders(token), params, paramsSerializer: { indexes: null } },
    );
    return res?.data;
}

async function getFunderAudit(token, funderId) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/funders/${funderId}/audit`,
        authHeaders(token),
    );
    return res?.data;
}

// --- Loans -------------------------------------------------------------

async function getLoans(token, { showDeleted = false } = {}) {
    const res = await axios.get(`${baseUrl}/funder-allocation/loans`, {
        ...authHeaders(token),
        params: { show_deleted: showDeleted },
    });
    return res?.data;
}

async function getLoan(token, loanId) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/loans/${loanId}`,
        authHeaders(token),
    );
    return res?.data;
}

async function createLoan(token, { ref, name, amountCents, status }) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/loans`,
        { ref, name, amountCents, status },
        authHeaders(token),
    );
    return res?.data;
}

async function updateLoan(token, loanId, { ref, name, amountCents, status }) {
    const res = await axios.put(
        `${baseUrl}/funder-allocation/loans/${loanId}`,
        { ref, name, amountCents, status },
        authHeaders(token),
    );
    return res?.data;
}

async function deleteLoan(token, loanId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/loans/${loanId}`,
        authHeaders(token),
    );
    return res?.data;
}

async function restoreLoan(token, loanId) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/loans/${loanId}/restore`,
        null,
        authHeaders(token),
    );
    return res?.data;
}

async function getLoanAllocations(token, loanId, { showDeleted = false } = {}) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/loans/${loanId}/allocations`,
        { ...authHeaders(token), params: { show_deleted: showDeleted } },
    );
    return res?.data;
}

// allocations: [{ funderId, portionCents, effectiveDate, previousAllocationId }]
async function saveLoanAllocations(token, loanId, allocations) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/loans/${loanId}/allocations`,
        { allocations },
        authHeaders(token),
    );
    return res?.data;
}

async function getLoanAudit(token, loanId) {
    const res = await axios.get(
        `${baseUrl}/funder-allocation/loans/${loanId}/audit`,
        authHeaders(token),
    );
    return res?.data;
}

// --- Allocations (mistake-delete, independent of reallocation) -------------

async function deleteAllocation(token, allocationId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/allocations/${allocationId}`,
        authHeaders(token),
    );
    return res?.data;
}

async function restoreAllocation(token, allocationId) {
    const res = await axios.post(
        `${baseUrl}/funder-allocation/allocations/${allocationId}/restore`,
        null,
        authHeaders(token),
    );
    return res?.data;
}

// --- Admin-only permanent delete ---------------------------------------

async function hardDeleteFunder(token, funderId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/funders/${funderId}/hard`,
        authHeaders(token),
    );
    return res?.data;
}

async function hardDeleteLoan(token, loanId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/loans/${loanId}/hard`,
        authHeaders(token),
    );
    return res?.data;
}

async function hardDeleteAllocation(token, allocationId) {
    const res = await axios.delete(
        `${baseUrl}/funder-allocation/allocations/${allocationId}/hard`,
        authHeaders(token),
    );
    return res?.data;
}

export {
    getFunders,
    getFunder,
    createFunder,
    updateFunder,
    deleteFunder,
    restoreFunder,
    getFunderAllocations,
    getFunderAudit,
    getLoans,
    getLoan,
    createLoan,
    updateLoan,
    deleteLoan,
    restoreLoan,
    getLoanAllocations,
    saveLoanAllocations,
    getLoanAudit,
    deleteAllocation,
    restoreAllocation,
    hardDeleteFunder,
    hardDeleteLoan,
    hardDeleteAllocation,
    getAllAudit,
};
